/**
 * Process Visual Editor
 * 
 * Visual drag-and-drop editor for complex process definitions
 * Allows users to create and modify process flows visually
 */

// Import from our new data layer
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    ColorInput,
    Divider,
    Group,
    Modal,
    MultiSelect,
    Paper,
    ScrollArea,
    SegmentedControl,
    Select,
    Stack,
    Switch,
    Text,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import {
    IconArrowsSplit,
    IconCheck,
    IconDatabase,
    IconEdit,
    IconLayersLinked,
    IconPlus,
    IconRefresh,
    IconRoute,
    IconSettings,
    IconTrash,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useProcessMutations } from '../data';

// Types
interface ProcessStage {
    id: string;
    name: string;
    stageType: 'Entry' | 'Process' | 'Decision' | 'External' | 'Loop' | 'Meta' | 'Exit';
    actors: string[];
    steps: ProcessStep[];
    loops: boolean;
    parallelTracks?: string[];
    estimatedDuration?: string;
    position?: { x: number; y: number }; // For visual positioning
    color?: string; // Visual customization
}

interface ProcessStep {
    id: string;
    name: string;
    actor: string;
    stepType?: string;
    external: boolean;
    conditional?: boolean;
    loops?: boolean;
    metadata?: any;
}

interface ProcessConnection {
    id?: string;
    from: string;
    to: string;
    connectionType: 'Sequential' | 'Loop' | 'Conditional' | 'Parallel' | 'Escalation';
    condition?: string;
    label?: string;
}

interface ExternalSystem {
    id: string;
    name: string;
    systemType: string;
    integrationPoints: string[];
    dataFormats: string[];
}

interface ProcessVisualEditorProps {
    processId?: string;
    initialData?: {
        id?: string;
        name: string;
        domain: string;
        description: string;
        complexity: string;
        avgDuration?: string;
        stages: ProcessStage[];
        connections: ProcessConnection[];
        actors: string[];
        externalSystems: ExternalSystem[];
        metadata?: any;
    };
    onSave?: (data: any) => void;
    onCancel?: () => void;
    readOnly?: boolean;
}

// Stage color schemes
const STAGE_COLORS = {
    Entry: '#10b981',
    Process: '#3b82f6',
    Decision: '#f59e0b',
    External: '#8b5cf6',
    Loop: '#14b8a6',
    Meta: '#ec4899',
    Exit: '#ef4444'
};

// Draggable stage component
const DraggableStage: React.FC<{
    stage: ProcessStage;
    onEdit: () => void;
    onDelete: () => void;
    onConnect: () => void;
    isSelected: boolean;
    readOnly?: boolean;
}> = ({ stage, onEdit, onDelete, onConnect, isSelected, readOnly }) => {
    const [{ isDragging }, drag] = useDrag({
        type: 'stage',
        item: { id: stage.id, type: 'stage' },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    const stageColor = stage.color || STAGE_COLORS[stage.stageType] || '#6b7280';

    return (
        <Box
            ref={drag}
            style={{
                position: 'absolute',
                left: stage.position?.x || 0,
                top: stage.position?.y || 0,
                opacity: isDragging ? 0.5 : 1,
                cursor: readOnly ? 'default' : 'move',
                zIndex: isSelected ? 10 : 1
            }}
        >
            <Card
                withBorder
                shadow={isSelected ? 'lg' : 'sm'}
                p="sm"
                style={{
                    borderColor: isSelected ? stageColor : undefined,
                    borderWidth: isSelected ? 2 : 1,
                    minWidth: 180,
                    maxWidth: 220
                }}
            >
                <Group justify="space-between" mb="xs">
                    <Badge color={stageColor} variant="filled" size="sm">
                        {stage.stageType}
                    </Badge>
                    {!readOnly && (
                        <Group gap={2}>
                            <ActionIcon size="xs" variant="subtle" onClick={onConnect}>
                                <IconRoute size={14} />
                            </ActionIcon>
                            <ActionIcon size="xs" variant="subtle" onClick={onEdit}>
                                <IconEdit size={14} />
                            </ActionIcon>
                            <ActionIcon size="xs" variant="subtle" color="red" onClick={onDelete}>
                                <IconTrash size={14} />
                            </ActionIcon>
                        </Group>
                    )}
                </Group>

                <Text size="sm" fw={500} lineClamp={1}>
                    {stage.name}
                </Text>

                {stage.actors.length > 0 && (
                    <Text size="xs" c="dimmed" mt={4}>
                        {stage.actors.join(', ')}
                    </Text>
                )}

                <Group gap={4} mt="xs">
                    {stage.loops && (
                        <Tooltip label="Repeating stage">
                            <IconRefresh size={14} />
                        </Tooltip>
                    )}
                    {stage.steps.some(s => s.external) && (
                        <Tooltip label="Has external integrations">
                            <IconDatabase size={14} />
                        </Tooltip>
                    )}
                    {stage.parallelTracks && stage.parallelTracks.length > 0 && (
                        <Tooltip label="Has parallel tracks">
                            <IconArrowsSplit size={14} />
                        </Tooltip>
                    )}
                </Group>

                <Text size="xs" c="dimmed" mt="xs">
                    {stage.steps.length} steps
                </Text>
            </Card>
        </Box>
    );
};

// Canvas drop zone
const CanvasDropZone: React.FC<{
    onDrop: (item: any, position: { x: number; y: number }) => void;
    children: React.ReactNode;
}> = ({ onDrop, children }) => {
    const canvasRef = useRef<HTMLDivElement>(null);

    const [, drop] = useDrop({
        accept: 'stage',
        drop: (item: any, monitor) => {
            const offset = monitor.getClientOffset();
            if (offset && canvasRef.current) {
                const rect = canvasRef.current.getBoundingClientRect();
                onDrop(item, {
                    x: offset.x - rect.left,
                    y: offset.y - rect.top
                });
            }
        }
    });

    drop(canvasRef);

    return (
        <Box
            ref={canvasRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                background: '#f8f9fa',
                backgroundImage: 'radial-gradient(circle, #dee2e6 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}
        >
            {children}
        </Box>
    );
};

export const ProcessVisualEditor: React.FC<ProcessVisualEditorProps> = ({
    processId,
    initialData,
    onSave,
    onCancel,
    readOnly = false
}) => {
    // Use our new repository pattern
    const { createProcess, updateProcess, loading: mutationLoading } = useProcessMutations();
    const [stages, setStages] = useState<ProcessStage[]>(initialData?.stages || []);
    const [connections, setConnections] = useState<ProcessConnection[]>(initialData?.connections || []);
    const [selectedStage, setSelectedStage] = useState<string | null>(null);
    const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
    const [showStageModal, setShowStageModal] = useState(false);
    const [showConnectionModal, setShowConnectionModal] = useState(false);
    const [editingStage, setEditingStage] = useState<ProcessStage | null>(null);
    const [editingConnection, setEditingConnection] = useState<ProcessConnection | null>(null);
    const [actors, setActors] = useState<string[]>(initialData?.actors || []);
    const [externalSystems, setExternalSystems] = useState<ExternalSystem[]>(initialData?.externalSystems || []);
    const [viewMode, setViewMode] = useState<'design' | 'preview'>('design');
    const [zoom, setZoom] = useState(100);

    // Process metadata form
    const processForm = useForm({
        initialValues: {
            name: initialData?.name || '',
            domain: initialData?.domain || 'Healthcare',
            description: initialData?.description || '',
            complexity: initialData?.complexity || 'Medium',
            avgDuration: initialData?.avgDuration || ''
        }
    });

    // Stage form
    const stageForm = useForm({
        initialValues: {
            name: '',
            stageType: 'Process',
            actors: [] as string[],
            loops: false,
            estimatedDuration: '',
            parallelTracks: [] as string[],
            color: ''
        }
    });

    // Connection form
    const connectionForm = useForm({
        initialValues: {
            from: '',
            to: '',
            connectionType: 'Sequential',
            condition: '',
            label: ''
        }
    });

    // Handle stage drop
    const handleStageDrop = useCallback((item: { id: string }, position: { x: number; y: number }) => {
        const stage = stages.find(s => s.id === item.id);
        if (stage) {
            setStages(prev => prev.map(s =>
                s.id === item.id ? { ...s, position } : s
            ));
        }
    }, [stages]);

    // Add new stage
    const handleAddStage = (values: typeof stageForm.values) => {
        const newStage: ProcessStage = {
            id: `stage_${Date.now()}`,
            name: values.name,
            stageType: values.stageType as any,
            actors: values.actors,
            steps: [],
            loops: values.loops,
            parallelTracks: values.parallelTracks.filter((t: string | null | undefined) => Boolean(t)) as string[],
            estimatedDuration: values.estimatedDuration,
            position: { x: 100 + stages.length * 50, y: 100 + stages.length * 50 },
            color: values.color || undefined
        };

        setStages([...stages, newStage]);
        setShowStageModal(false);
        stageForm.reset();

        // Add new actors to the list
        const newActors = values.actors.filter((a: string) => !actors.includes(a));
        if (newActors.length > 0) {
            setActors([...actors, ...newActors]);
        }
    };

    // Update stage
    const handleUpdateStage = (values: typeof stageForm.values) => {
        if (!editingStage) return;

        setStages(prev => prev.map((s: ProcessStage) =>
            s.id === editingStage.id ? {
                ...s,
                name: values.name,
                stageType: values.stageType as any,
                actors: values.actors,
                loops: values.loops,
                parallelTracks: values.parallelTracks.filter((t: string | null | undefined) => Boolean(t)) as string[],
                estimatedDuration: values.estimatedDuration,
                color: values.color || undefined
            } : s
        ));

        setShowStageModal(false);
        setEditingStage(null);
        stageForm.reset();
    };

    // Delete stage
    const handleDeleteStage = (stageId: string) => {
        setStages(prev => prev.filter(s => s.id !== stageId));
        setConnections(prev => prev.filter(c => c.from !== stageId && c.to !== stageId));
    };

    // Add connection
    const handleAddConnection = (values: typeof connectionForm.values) => {
        const newConnection: ProcessConnection = {
            id: `conn_${Date.now()}`,
            ...values,
            connectionType: values.connectionType as any
        };

        setConnections([...connections, newConnection]);
        setShowConnectionModal(false);
        connectionForm.reset();
    };

    // Save process
    const handleSave = async () => {
        const processData: Record<string, unknown> = {
            ...processForm.values,
            stages,
            connections,
            actors: [...new Set(stages.flatMap(s => s.actors))],
            externalSystems,
            metadata: {
                editorVersion: '1.0.0',
                lastModified: new Date().toISOString()
            }
        };

        if (onSave) {
            onSave(processData);
        } else {
            // Save using repository pattern
            try {
                if (processId) {
                    // Update existing
                    await updateProcess(processId, processData as Parameters<typeof updateProcess>[1]);
                } else {
                    // Create new
                    await createProcess(processData as Parameters<typeof createProcess>[0]);
                }

                showNotification({
                    title: 'Success',
                    message: 'Process saved successfully',
                    color: 'green',
                    icon: <IconCheck />
                });
            } catch (error) {
                console.error('Failed to save process:', error);
                showNotification({
                    title: 'Error',
                    message: 'Failed to save process',
                    color: 'red',
                    icon: <IconX />
                });
            }
        }
    };

    // Render connection lines
    const renderConnections = () => {
        return connections.map(conn => {
            const fromStage = stages.find(s => s.id === conn.from);
            const toStage = stages.find(s => s.id === conn.to);

            if (!fromStage || !toStage || !fromStage.position || !toStage.position) return null;

            const x1 = fromStage.position.x + 90;
            const y1 = fromStage.position.y + 40;
            const x2 = toStage.position.x + 90;
            const y2 = toStage.position.y + 40;

            const isLoop = conn.connectionType === 'Loop';
            const color = isLoop ? '#f59e0b' :
                conn.connectionType === 'Conditional' ? '#10b981' :
                    conn.connectionType === 'Parallel' ? '#8b5cf6' :
                        '#6b7280';

            return (
                <g key={conn.id}>
                    <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={color}
                        strokeWidth={selectedConnection === conn.id ? 3 : 2}
                        strokeDasharray={isLoop ? '5,5' : undefined}
                        markerEnd={`url(#arrow-${color.replace('#', '')})`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedConnection(conn.id || null)}
                    />
                    {conn.label && (
                        <text
                            x={(x1 + x2) / 2}
                            y={(y1 + y2) / 2 - 5}
                            textAnchor="middle"
                            fontSize="12"
                            fill={color}
                        >
                            {conn.label}
                        </text>
                    )}
                </g>
            );
        });
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Box style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Paper p="md" shadow="sm">
                    <Group justify="space-between">
                        <Group>
                            <IconLayersLinked size={24} />
                            <div>
                                <Title order={4}>Process Visual Editor</Title>
                                <Text size="sm" c="dimmed">
                                    {processForm.values.name || 'Untitled Process'}
                                </Text>
                            </div>
                        </Group>

                        <Group>
                            <SegmentedControl
                                value={viewMode}
                                onChange={(v: string) => setViewMode((v as 'design' | 'preview'))}
                                data={[
                                    { label: 'Design', value: 'design' },
                                    { label: 'Preview', value: 'preview' }
                                ]}
                            />

                            <Button.Group>
                                <Button
                                    variant="light"
                                    leftSection={<IconSettings size={16} />}
                                    onClick={() => {
                                        // Open process settings
                                    }}
                                >
                                    Settings
                                </Button>
                                <Button
                                    leftSection={<IconCheck size={16} />}
                                    onClick={handleSave}
                                    disabled={readOnly}
                                >
                                    Save
                                </Button>
                                {onCancel && (
                                    <Button
                                        variant="subtle"
                                        onClick={onCancel}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </Button.Group>
                        </Group>
                    </Group>
                </Paper>

                {/* Main content */}
                <Box style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {/* Left sidebar - Toolbox */}
                    {viewMode === 'design' && !readOnly && (
                        <Paper p="md" style={{ width: 250, overflow: 'auto' }}>
                            <Stack>
                                <div>
                                    <Title order={5} mb="xs">Add Stage</Title>
                                    <Button
                                        fullWidth
                                        variant="light"
                                        leftSection={<IconPlus size={16} />}
                                        onClick={() => {
                                            setEditingStage(null);
                                            stageForm.reset();
                                            setShowStageModal(true);
                                        }}
                                    >
                                        New Stage
                                    </Button>
                                </div>

                                <Divider />

                                <div>
                                    <Title order={5} mb="xs">Add Connection</Title>
                                    <Button
                                        fullWidth
                                        variant="light"
                                        leftSection={<IconRoute size={16} />}
                                        onClick={() => {
                                            connectionForm.reset();
                                            setShowConnectionModal(true);
                                        }}
                                        disabled={stages.length < 2}
                                    >
                                        New Connection
                                    </Button>
                                </div>

                                <Divider />

                                <div>
                                    <Title order={5} mb="xs">Stage Templates</Title>
                                    <Stack gap="xs">
                                        {Object.keys(STAGE_COLORS).map(type => (
                                            <Button
                                                key={type}
                                                fullWidth
                                                variant="subtle"
                                                size="xs"
                                                leftSection={
                                                    <Box
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            background: STAGE_COLORS[type as keyof typeof STAGE_COLORS]
                                                        }}
                                                    />
                                                }
                                                onClick={() => {
                                                    setEditingStage(null);
                                                    stageForm.setValues({
                                                        name: '',
                                                        stageType: type,
                                                        actors: [],
                                                        loops: false,
                                                        estimatedDuration: '',
                                                        parallelTracks: [],
                                                        color: STAGE_COLORS[type as keyof typeof STAGE_COLORS]
                                                    });
                                                    setShowStageModal(true);
                                                }}
                                            >
                                                {type}
                                            </Button>
                                        ))}
                                    </Stack>
                                </div>
                            </Stack>
                        </Paper>
                    )}

                    {/* Canvas */}
                    <Box style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                        <ScrollArea style={{ height: '100%' }}>
                            <Box
                                style={{
                                    transform: `scale(${zoom / 100})`,
                                    transformOrigin: 'top left',
                                    width: `${100 * (100 / zoom)}%`,
                                    height: `${100 * (100 / zoom)}%`,
                                    minHeight: 800
                                }}
                            >
                                <CanvasDropZone onDrop={handleStageDrop}>
                                    {/* SVG for connections */}
                                    <svg
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {/* Define arrow markers */}
                                        <defs>
                                            {Object.values(STAGE_COLORS).map(color => (
                                                <marker
                                                    key={color}
                                                    id={`arrow-${color.replace('#', '')}`}
                                                    markerWidth="10"
                                                    markerHeight="10"
                                                    refX="9"
                                                    refY="3"
                                                    orient="auto"
                                                >
                                                    <polygon
                                                        points="0 0, 10 3, 0 6"
                                                        fill={color}
                                                    />
                                                </marker>
                                            ))}
                                        </defs>

                                        {/* Render connections */}
                                        <g style={{ pointerEvents: 'all' }}>
                                            {renderConnections()}
                                        </g>
                                    </svg>

                                    {/* Render stages */}
                                    {stages.map(stage => (
                                        <DraggableStage
                                            key={stage.id}
                                            stage={stage}
                                            isSelected={selectedStage === stage.id}
                                            readOnly={readOnly || viewMode === 'preview'}
                                            onEdit={() => {
                                                setEditingStage(stage);
                                                stageForm.setValues({
                                                    name: stage.name,
                                                    stageType: stage.stageType,
                                                    actors: stage.actors,
                                                    loops: stage.loops,
                                                    estimatedDuration: stage.estimatedDuration || '',
                                                    parallelTracks: stage.parallelTracks || [],
                                                    color: stage.color || ''
                                                });
                                                setShowStageModal(true);
                                            }}
                                            onDelete={() => handleDeleteStage(stage.id)}
                                            onConnect={() => {
                                                connectionForm.setFieldValue('from', stage.id);
                                                setShowConnectionModal(true);
                                            }}
                                        />
                                    ))}
                                </CanvasDropZone>
                            </Box>
                        </ScrollArea>

                        {/* Zoom controls */}
                        <Paper
                            p="xs"
                            shadow="sm"
                            style={{
                                position: 'absolute',
                                bottom: 20,
                                right: 20
                            }}
                        >
                            <Group gap="xs">
                                <ActionIcon
                                    variant="light"
                                    onClick={() => setZoom(Math.max(50, zoom - 10))}
                                >
                                    -
                                </ActionIcon>
                                <Text size="sm" style={{ width: 50, textAlign: 'center' }}>
                                    {zoom}%
                                </Text>
                                <ActionIcon
                                    variant="light"
                                    onClick={() => setZoom(Math.min(200, zoom + 10))}
                                >
                                    +
                                </ActionIcon>
                            </Group>
                        </Paper>
                    </Box>

                    {/* Right sidebar - Properties */}
                    {viewMode === 'design' && (selectedStage || selectedConnection) && (
                        <Paper p="md" style={{ width: 300, overflow: 'auto' }}>
                            {selectedStage && (
                                <Stack>
                                    <Title order={5}>Stage Properties</Title>
                                    <Text size="sm">
                                        {stages.find(s => s.id === selectedStage)?.name}
                                    </Text>
                                    <Button
                                        size="sm"
                                        variant="light"
                                        onClick={() => {
                                            const stage = stages.find(s => s.id === selectedStage);
                                            if (stage) {
                                                setEditingStage(stage);
                                                stageForm.setValues({
                                                    name: stage.name,
                                                    stageType: stage.stageType,
                                                    actors: stage.actors,
                                                    loops: stage.loops,
                                                    estimatedDuration: stage.estimatedDuration || '',
                                                    parallelTracks: stage.parallelTracks || [],
                                                    color: stage.color || ''
                                                });
                                                setShowStageModal(true);
                                            }
                                        }}
                                    >
                                        Edit Stage
                                    </Button>
                                </Stack>
                            )}

                            {selectedConnection && (
                                <Stack>
                                    <Title order={5}>Connection Properties</Title>
                                    <Text size="sm">
                                        {connections.find(c => c.id === selectedConnection)?.label || 'Unnamed'}
                                    </Text>
                                </Stack>
                            )}
                        </Paper>
                    )}
                </Box>

                {/* Stage Modal */}
                <Modal
                    opened={showStageModal}
                    onClose={() => setShowStageModal(false)}
                    title={editingStage ? 'Edit Stage' : 'Add Stage'}
                    size="lg"
                >
                    <form onSubmit={stageForm.onSubmit(editingStage ? handleUpdateStage : handleAddStage)}>
                        <Stack>
                            <TextInput
                                label="Stage Name"
                                required
                                {...stageForm.getInputProps('name')}
                            />

                            <Select
                                label="Stage Type"
                                required
                                data={Object.keys(STAGE_COLORS)}
                                {...stageForm.getInputProps('stageType')}
                            />

                            <MultiSelect
                                label="Actors"
                                data={actors}
                                searchable
                                {...stageForm.getInputProps('actors')}
                            />

                            <Switch
                                label="Can repeat (loops)"
                                {...stageForm.getInputProps('loops', { type: 'checkbox' })}
                            />

                            <TextInput
                                label="Estimated Duration"
                                placeholder="e.g., 1-2 days"
                                {...stageForm.getInputProps('estimatedDuration')}
                            />

                            <ColorInput
                                label="Custom Color"
                                format="hex"
                                {...stageForm.getInputProps('color')}
                            />

                            <Group justify="flex-end">
                                <Button variant="light" onClick={() => setShowStageModal(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit">
                                    {editingStage ? 'Update' : 'Add'} Stage
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal>

                {/* Connection Modal */}
                <Modal
                    opened={showConnectionModal}
                    onClose={() => setShowConnectionModal(false)}
                    title="Add Connection"
                >
                    <form onSubmit={connectionForm.onSubmit(handleAddConnection)}>
                        <Stack>
                            <Select
                                label="From Stage"
                                required
                                data={stages.map(s => ({ value: s.id, label: s.name }))}
                                {...connectionForm.getInputProps('from')}
                            />

                            <Select
                                label="To Stage"
                                required
                                data={stages.map(s => ({ value: s.id, label: s.name }))}
                                {...connectionForm.getInputProps('to')}
                            />

                            <Select
                                label="Connection Type"
                                required
                                data={['Sequential', 'Loop', 'Conditional', 'Parallel', 'Escalation']}
                                {...connectionForm.getInputProps('connectionType')}
                            />

                            <TextInput
                                label="Condition"
                                placeholder="e.g., approved, denied"
                                {...connectionForm.getInputProps('condition')}
                            />

                            <TextInput
                                label="Label"
                                placeholder="Optional label for connection"
                                {...connectionForm.getInputProps('label')}
                            />

                            <Group justify="flex-end">
                                <Button variant="light" onClick={() => setShowConnectionModal(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit">
                                    Add Connection
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal>
            </Box>
        </DndProvider>
    );
};

export default ProcessVisualEditor;
